import { useEffect, useRef, useState } from "react";
import { fabric } from "fabric";
import Swal from "sweetalert2";
import "./FabricEditor.scss";
import combinedServices from "../../shared/services/user-service";
import Header from "../clubDashboard/Header/Header";
import { Link as LinkPage } from "react-router-dom";

const FabricEditor = () => {
  const canvasRef = useRef(null);
  const backCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const [backCanvas, setBackCanvas] = useState(null);
  const [textInput, setTextInput] = useState("");
  const [textColor, setTextColor] = useState("#000000");
  const [fontFamily, setFontFamily] = useState("Arial");
  const [fontSize, setFontSize] = useState(16);
  const [bgColor, setBgColor] = useState("#ffffff");
  const [cardWidth, setCardWidth] = useState(324);
  const [cardHeight, setCardHeight] = useState(204);
  const [isFrontSelected, setIsFrontSelected] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [nameCardTemplates, setNameCardTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [dynamicFields, setDynamicFields] = useState([
    { id: "name", label: "Name", value: "{{name}}", selected: false },
    { id: "userId", label: "User Id", value: "{{userId}}", selected: false },
    { id: "email", label: "Email", value: "{{email}}", selected: false },
    { id: "phone", label: "Phone", value: "{{phone}}", selected: false },
    { id: "qrCode", label: "QR Code", value: "{{qrCode}}", selected: false },
    { id:"nambaNumber", label:"Namba Number", value:"{{nambaNumber}}",selected:false}
  ]);

  // Load templates from database on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      const res = await combinedServices.getNameCardTemplates();
      if (res && res.data) {
        setNameCardTemplates(res.data);
      }
    };
    fetchTemplates();
  }, []);

  // Initialize canvases (runs only once on mount)
  useEffect(() => {
    const newCanvas = new fabric.Canvas(canvasRef.current, {
      backgroundColor: bgColor,
      preserveObjectStacking: true,
      width: cardWidth,
      height: cardHeight,
      selection: true,
      selectionBorderColor: "#3f51b5",
      selectionLineWidth: 2,
    });

    const newBackCanvas = new fabric.Canvas(backCanvasRef.current, {
      backgroundColor: bgColor,
      preserveObjectStacking: true,
      width: cardWidth,
      height: cardHeight,
      selection: true,
      selectionBorderColor: "#3f51b5",
      selectionLineWidth: 2,
    });

    // Set up common properties for both front and back canvases
    fabric.Object.prototype.set({
      borderColor: "#3f51b5",
      cornerColor: "#fff",
      cornerStrokeColor: "#3f51b5",
      cornerSize: 10,
      transparentCorners: false,
      cornerStyle: "circle",
      borderScaleFactor: 1.5,
      padding: 5,
    });

    setCanvas(newCanvas);
    setBackCanvas(newBackCanvas);

    return () => {
      newCanvas.dispose();
      newBackCanvas.dispose();
    };
  }, []);

  // Update background color without recreating canvas
  useEffect(() => {
    if (canvas) {
      canvas.setBackgroundColor(bgColor, canvas.renderAll.bind(canvas));
    }
    if (backCanvas) {
      backCanvas.setBackgroundColor(
        bgColor,
        backCanvas.renderAll.bind(backCanvas)
      );
    }
  }, [bgColor, canvas, backCanvas]);

  // Update canvas dimensions without recreating canvas
  useEffect(() => {
    if (canvas) {
      canvas.setWidth(cardWidth);
      canvas.setHeight(cardHeight);
      canvas.renderAll();
    }
    if (backCanvas) {
      backCanvas.setWidth(cardWidth);
      backCanvas.setHeight(cardHeight);
      backCanvas.renderAll();
    }
  }, [cardWidth, cardHeight, canvas, backCanvas]);

  const addText = () => {
    if (!canvas) return;

    const text = new fabric.Textbox(textInput || "Edit text", {
      left: 20,
      top: 20,
      width: cardWidth - 40,
      fontSize,
      fontFamily,
      fill: textColor,
      editable: true,
      hasControls: true,
      hasBorders: true,
      padding: 5,
      lockScalingFlip: true,
      scaleX: 1,
      scaleY: 1,
      lockUniScaling: false,
      textAlign: "center",
      styles: {},
    });

    text.setControlsVisibility({
      mt: true,
      mb: true,
      ml: true,
      mr: true,
      mtr: true,
    });

    const activeCanvas = isFrontSelected ? canvas : backCanvas;
    activeCanvas.add(text);
    activeCanvas.setActiveObject(text);
    text.enterEditing();
    text.selectAll();
    activeCanvas.renderAll();

    const textInputElement = document.querySelector('input[type="text"]');
    if (textInputElement) textInputElement.focus();
  };

  const addDynamicField = async (field) => {
    if (!canvas) return;

    if (field.id === "qrCode") {
      await addQRCode();
      return;
    }

    const text = new fabric.Textbox(field.value, {
      left: 20,
      top: 20,
      width: cardWidth - 40,
      fontSize,
      fontFamily,
      fill: textColor,
      editable: true,
      hasControls: true,
      hasBorders: true,
      padding: 5,
      lockScalingFlip: true,
      scaleX: 1,
      scaleY: 1,
      lockUniScaling: false,
      textAlign: "center",
      styles: {},
      data: { isDynamicField: true, fieldId: field.id },
    });

    text.setControlsVisibility({
      mt: true,
      mb: true,
      ml: true,
      mr: true,
      mtr: true,
    });

    const activeCanvas = isFrontSelected ? canvas : backCanvas;
    activeCanvas.add(text);
    activeCanvas.setActiveObject(text);
    activeCanvas.renderAll();
  };

  const addQRCode = async () => {
    if (!canvas) return;

    try {
      fabric.Image.fromURL(
        "https://nambaweb.s3.ap-south-1.amazonaws.com/Dynamic%20Data/1745585747052-q5fIo.png",
        (img) => {
          const qrSize = Math.min(cardWidth, cardHeight) * 0.3;

          img.set({
            left: cardWidth - qrSize - 20,
            top: cardHeight - qrSize - 20,
            scaleX: qrSize / img.width,
            scaleY: qrSize / img.height,
            hasControls: true,
            hasBorders: true,
            lockScalingFlip: true,
            cornerSize: 10,
            transparentCorners: false,
            data: {
              isDynamicField: true,
              fieldId: "qrCode",
              isQRCode: true,
            },
          });

          const activeCanvas = isFrontSelected ? canvas : backCanvas;
          activeCanvas.add(img);
          activeCanvas.setActiveObject(img);
          activeCanvas.renderAll();
        }
      );
    } catch (error) {
      console.error("Error generating QR code:", error);
      Swal.fire({
        icon: "error",
        title: "Error Generating QR Code",
        text: "Failed to generate QR code. Please try again.",
      });
    }
  };

  const deleteSelected = () => {
    if (!canvas) return;
    const activeCanvas = isFrontSelected ? canvas : backCanvas;
    const activeObject = activeCanvas.getActiveObject();
    if (activeObject) activeCanvas.remove(activeObject);
  };

  const bringToFront = () => {
    if (!canvas) return;
    const activeCanvas = isFrontSelected ? canvas : backCanvas;
    const activeObject = activeCanvas.getActiveObject();
    if (activeObject) {
      activeObject.bringToFront();
      activeCanvas.renderAll();
    }
  };

  const sendToBack = () => {
    if (!canvas) return;
    const activeCanvas = isFrontSelected ? canvas : backCanvas;
    const activeObject = activeCanvas.getActiveObject();
    if (activeObject) {
      activeObject.sendToBack();
      activeCanvas.renderAll();
    }
  };

  const handleImageUpload = async (file) => {
    if (!file || !canvas) return;

    try {
      // Show loading indicator
      Swal.fire({
        title: "Uploading Image",
        html: "Please wait...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Upload to S3
      const imageUrl = await combinedServices.uploadImageToS3(file);

      // Create fabric image from S3 URL
      // fabric.Image.fromURL('https://upload.wikimedia.org/wikipedia/commons/4/47/PNG_transparency_demonstration_1.png', (img) => {
      fabric.Image.fromURL(
        imageUrl,
        (img) => {
          const maxWidth = cardWidth * 0.8;
          const maxHeight = cardHeight * 0.8;

          if (img.width > maxWidth || img.height > maxHeight) {
            const scale = Math.min(
              maxWidth / img.width,
              maxHeight / img.height
            );
            img.scale(scale);
          }

          img.set({
            left: cardWidth / 2 - (img.width * img.scaleX) / 2,
            top: cardHeight / 2 - (img.height * img.scaleY) / 2,
            hasControls: true,
            lockScalingFlip: true,
            cornerSize: 10,
            transparentCorners: false,
            originX: "center",
            originY: "center",
            data: {
              isImage: true,
              imageUrl: imageUrl, // Store the S3 URL in the object data
            },
          });

          const activeCanvas = isFrontSelected ? canvas : backCanvas;
          activeCanvas.add(img);
          activeCanvas.setActiveObject(img);
          activeCanvas.renderAll();

          Swal.close();
        },
        {
          crossOrigin: "anonymous", // Important for CORS
        }
      );
    } catch (error) {
      console.error("Error uploading image:", error);
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Failed to upload image. Please try again.",
      });
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleImageUpload(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) handleImageUpload(files[0]);
  };

  const selectFront = () => {
    setIsFrontSelected(true);
  };

  const selectBack = () => {
    setIsFrontSelected(false);
  };

  const saveCanvas = async () => {
    if (!canvas || !backCanvas) return;

    const currentTemplate = nameCardTemplates.find(
      (t) => t.id == selectedTemplateId
    );
    const defaultTemplateName = currentTemplate
      ? currentTemplate.template_name
      : "";

    const { value: templateName } = await Swal.fire({
      title: "Please enter template name",
      input: "text",
      inputPlaceholder: "Template name",
      inputValue: defaultTemplateName,
      showCancelButton: true,
      confirmButtonText: "Save Template",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value) {
          return "Template name is required";
        }
      },
    });

    if (templateName) {
      try {
        // Serialize canvas data while replacing image objects with references
        const frontCanvasData = JSON.stringify(canvas, (key, value) => {
          if (key === "canvas" || key === "_objects") return undefined;
          if (
            value &&
            value.type === "image" &&
            value.data &&
            value.data.imageUrl
          ) {
            return {
              ...value,
              src: value.data.imageUrl, // Replace base64 with S3 URL
              customType: "s3ImageReference", // Mark as S3 reference
            };
          }
          return value;
        });

        const backCanvasData = JSON.stringify(backCanvas, (key, value) => {
          if (key === "canvas" || key === "_objects") return undefined;
          if (
            value &&
            value.type === "image" &&
            value.data &&
            value.data.imageUrl
          ) {
            return {
              ...value,
              src: value.data.imageUrl,
              customType: "s3ImageReference",
            };
          }
          return value;
        });

        const response = await combinedServices.createNameCardTemplate({
          id: selectedTemplateId ? selectedTemplateId : null,
          template_name: templateName,
          front_canvas: frontCanvasData,
          back_canvas: backCanvasData,
        });

        if (response && response.data) {
          const res = await combinedServices.getNameCardTemplates();
          if (res && res.data) {
            setNameCardTemplates(res.data);
          }

          Swal.fire({
            icon: "success",
            title: "Template Saved Successfully!",
            text: `Template "${templateName}" has been saved to the database.`,
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error Saving Template",
          text: error.message || "Failed to save template",
        });
      }
    }
  };

  const loadTemplate = async (templateId) => {
    if (!canvas || !backCanvas) return;

    try {
      const response = await combinedServices.getNameCardTemplateById(
        templateId
      );
      if (response && response.data) {
        const template = response.data;

        // Clear existing canvas
        canvas.clear();
        backCanvas.clear();

        // Custom reviver function to handle S3 image references
        const reviver = (key, value) => {
          if (value && value.customType === "s3ImageReference") {
            return new Promise((resolve) => {
              fabric.Image.fromURL(
                value.src,
                (img) => {
                  // Restore all other properties
                  Object.keys(value).forEach((prop) => {
                    if (prop !== "src" && prop !== "customType") {
                      img[prop] = value[prop];
                    }
                  });
                  resolve(img);
                },
                {
                  crossOrigin: "anonymous",
                }
              );
            });
          }
          return value;
        };

        // Load front canvas data
        if (template.front_canvas) {
          const frontData = JSON.parse(template.front_canvas);
          await canvas.loadFromJSON(
            frontData,
            canvas.renderAll.bind(canvas),
            reviver
          );
        }

        // Load back canvas data
        if (template.back_canvas) {
          const backData = JSON.parse(template.back_canvas);
          await backCanvas.loadFromJSON(
            backData,
            backCanvas.renderAll.bind(backCanvas),
            reviver
          );
        }

        setSelectedTemplateId(templateId);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error Loading Template",
        text: error.message || "Failed to load template",
      });
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!templateId) return;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await combinedServices.deleteNameCardTemplate(
          templateId
        );
        if (response && response.data) {
          const res = await combinedServices.getNameCardTemplates();
          if (res && res.data) {
            setNameCardTemplates(res.data);
          }

          if (selectedTemplateId === templateId) {
            canvas.clear();
            backCanvas.clear();
            setSelectedTemplateId(null);
          }

          Swal.fire("Deleted!", "Your template has been deleted.", "success");
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error Deleting Template",
          text: error.message || "Failed to delete template",
        });
      }
    }
  };

  return (
    <div>
      <Header />
      <div className="dbRight">
        <div className="fabric-editor">
          <div className="name-card-editor" style={{margin:'10px',width:'fitContent'}}>
            <LinkPage to="/event-management" style={{ color: "gray" }}>
              Event Management
            </LinkPage>
            <LinkPage to="" style={{ color: "black" }}>
              Name Card Editor
            </LinkPage>
          </div>
          <h1>Name Card Design Editor</h1>
          <div className="editorCover">
            <div className="left">
              <div className="xyz">
                <ul className="flexBox createEventForm">
                  <li className="full">
                    <span className="fieldHd">Select Template</span>
                    <select
                      className="formSelect"
                      name="template"
                      value={selectedTemplateId || ""}
                      onChange={(e) => {
                        const templateId = e.target.value;
                        if (templateId) {
                          loadTemplate(templateId);
                        } else {
                          canvas.clear();
                          backCanvas.clear();
                          setSelectedTemplateId(null);
                        }
                      }}
                    >
                      <option value="">Select Template</option>
                      {nameCardTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.template_name}
                        </option>
                      ))}
                    </select>
                    {selectedTemplateId && (
                      <button
                        onClick={() => deleteTemplate(selectedTemplateId)}
                        className="toolbar-button danger"
                        style={{ marginLeft: "10px" }}
                      >
                        Delete Template
                      </button>
                    )}
                  </li>
                </ul>
              </div>

              <div className="editor-toolbar">
                <div className="toolbar-section">
                  <button onClick={addText} className="toolbar-button">
                    <i className="icon-text">T</i> Add Text
                  </button>
                  <input
                    type="text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Enter text"
                    className="text-input"
                  />
                  <div className="color-picker-container">
                    <label>Text Color:</label>
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="color-picker"
                    />
                  </div>
                  <select
                    value={fontFamily}
                    onChange={(e) => setFontFamily(e.target.value)}
                    className="font-select"
                  >
                    <option value="Arial">Arial</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Courier New">Courier New</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Verdana">Verdana</option>
                  </select>
                  <div className="font-size-container">
                    <label>Size:</label>
                    <input
                      type="number"
                      value={fontSize}
                      onChange={(e) =>
                        setFontSize(parseInt(e.target.value) || 16)
                      }
                      min="8"
                      max="48"
                      className="font-size-input"
                    />
                  </div>
                </div>

                <div className="toolbar-section">
                  {/* <button onClick={addQRCode} className="toolbar-button">
            <i className="icon-qr">QR</i> Add QR Code
          </button> */}
                  <button
                    onClick={deleteSelected}
                    className="toolbar-button danger"
                  >
                    <i className="icon-delete">×</i> Delete
                  </button>
                  <button onClick={bringToFront} className="toolbar-button">
                    <i className="icon-front">↑</i> Front
                  </button>
                  <button onClick={sendToBack} className="toolbar-button">
                    <i className="icon-back">↓</i> Back
                  </button>
                </div>

                <div className="toolbar-section">
                  <button onClick={saveCanvas} className="toolbar-button save">
                    <i className="icon-save">💾</i> Save
                  </button>
                </div>

                <div className="toolbar-section">
                  <div className="color-picker-container">
                    <label>Background:</label>
                    <input
                      type="color"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="color-picker"
                    />
                  </div>
                </div>

                <div className="toolbar-section dim">
                  <label>Card Width:</label>
                  <input
                    type="number"
                    value={cardWidth}
                    min="100"
                    max="1000"
                    onChange={(e) =>
                      setCardWidth(parseInt(e.target.value) || 324)
                    }
                    className="size-input"
                  />
                  <label>Card Height:</label>
                  <input
                    type="number"
                    value={cardHeight}
                    min="100"
                    max="1000"
                    onChange={(e) =>
                      setCardHeight(parseInt(e.target.value) || 204)
                    }
                    className="size-input"
                  />
                </div>
              </div>
            </div>
            <div className="right">
              <div className="dynamic-fields-section">
                <h3>Dynamic Fields</h3>
                <div className="dynamic-fields-container">
                  {dynamicFields.map((field) => (
                    <button
                      key={field.id}
                      onClick={() => addDynamicField(field)}
                      className="dynamic-field-button"
                    >
                      {field.label}
                    </button>
                  ))}
                </div>
                {/* <div className="alignment-buttons">
        <button onClick={() => alignDynamicFields('left')} className="alignment-button">
          Align Left
        </button>
        <button onClick={() => alignDynamicFields('center')} className="alignment-button">
          Align Center
        </button>
        <button onClick={() => alignDynamicFields('right')} className="alignment-button">
          Align Right
        </button>
        <button onClick={() => alignDynamicFields('vertical')} className="alignment-button">
          Distribute Vertically
        </button>
      </div> */}
              </div>

              <div
                className={`drop-area ${isDragging ? "highlight" : ""}`}
                onClick={() => fileInputRef.current.click()}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <p>Drag and drop images here or click to upload</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                  accept="image/*"
                  style={{ display: "none" }}
                />
              </div>

              <div className="canvas-container fabric-container">
                <div
                  className="canvas-side"
                  onClick={selectFront}
                  style={{
                    border: isFrontSelected ? "2px solid #3f51b5" : "none",
                    display: "inline-block",
                  }}
                >
                  <canvas
                    ref={canvasRef}
                    width={cardWidth}
                    height={cardHeight}
                  />
                </div>

                <div
                  className="canvas-side"
                  onClick={selectBack}
                  style={{
                    border: !isFrontSelected ? "2px solid #3f51b5" : "none",
                    display: "inline-block",
                    marginLeft: "20px",
                  }}
                >
                  <canvas
                    ref={backCanvasRef}
                    width={cardWidth}
                    height={cardHeight}
                  />
                </div>
              </div>

              <div className="size-info">
                <p>
                  Name Card Size: {cardWidth}px × {cardHeight}px (
                  {(cardWidth / 96).toFixed(2)}in ×{" "}
                  {(cardHeight / 96).toFixed(2)}in)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FabricEditor;
